import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

// Interface para os dados de evolução de receita
interface RevenueEvolution {
  labels: string[];
  datasets: {
    grossRevenue: number[];
    netRevenue: number[];
    salesCount?: number[];
  };
}

interface SalesListItem {
  id: string;
  status: string;
  amount: number;
  [key: string]: unknown;
}

// Interface para as props
interface StatisticsChartProps {
  salesData?: {
    summary: {
      totalSales: number;
      netRevenue: number;
      grossRevenue: number;
      paidOrders: number;
      averageTicket: number;
    };
    conversion: {
      creditCard: { total: number; percentage: number };
      pix: { total: number; percentage: number };
      boleto: { total: number; percentage: number };
      refunded: { total: number; percentage: number };
      chargeback: { total: number; percentage: number };
    };
    revenueEvolution: RevenueEvolution;
    salesList: SalesListItem[];
  } | null;
  isLoading?: boolean;
}

export default function StatisticsChart({ salesData, isLoading = false }: StatisticsChartProps) {
  // Criar array de zeros e datas vazias para quando não houver dados
  const emptyData: RevenueEvolution = {
    labels: [],
    datasets: {
      netRevenue: [],
      grossRevenue: [],
      salesCount: []
    }
  };

  // Usar dados reais da API ou array vazio se não houver dados
  const chartData = salesData?.revenueEvolution || emptyData;

  // Detectar se os labels são horários (contêm 'h' ou ':') ou datas
  const isHourlyData = chartData.labels.length > 0 && 
    (chartData.labels[0]?.includes('h') || chartData.labels[0]?.includes(':'));

  const options: ApexOptions = {
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
      fontSize: '14px',
      fontFamily: "Outfit, sans-serif",
      offsetY: 5,
    },
    colors: ["#465FFF"], // Define line colors - apenas uma cor agora
    chart: {
      fontFamily: "Outfit, sans-serif",
      height: 310,
      type: "area", // Set the chart type to 'area' for better visualization
      toolbar: {
        show: false, // Hide chart toolbar
      },
      animations: {
        enabled: true,
        speed: 800,
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      },
      parentHeightOffset: 0,
      zoom: {
        enabled: false
      },
    },
    stroke: {
      curve: "smooth", // Define the line style (straight, smooth, or step)
      width: [2], // Line width for each dataset
    },

    fill: {
      type: "gradient",
      gradient: {
        opacityFrom: 0.55,
        opacityTo: 0,
      },
    },
    markers: {
      size: 4,
      strokeColors: "#fff",
      strokeWidth: 2,
      hover: {
        size: 6,
      },
    },
    grid: {
      xaxis: {
        lines: {
          show: false, // Hide grid lines on x-axis
        },
      },
      yaxis: {
        lines: {
          show: true, // Show grid lines on y-axis
        },
      },
      padding: {
        top: 0,
        right: 10,
        bottom: 0,
        left: 0
      },
    },
    dataLabels: {
      enabled: false, // Disable data labels
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      style: {
        fontSize: '14px',
        fontFamily: "Outfit, sans-serif",
      },
      x: {
        formatter: function (_val: number, opts?: { dataPointIndex?: number }) {
          const idx = opts?.dataPointIndex ?? 0;
          const label = chartData.labels[idx] || '';
          const count = chartData.datasets.salesCount?.[idx];
          if (count !== undefined && count > 0) {
            return `${label} (${count} venda${count !== 1 ? 's' : ''})`;
          }
          return label;
        }
      },
      y: {
        formatter: function (value: number) {
          return value.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2
          });
        }
      }
    },
    xaxis: {
      type: "category", // Category-based x-axis
      categories: chartData.labels,
      labels: {
        style: {
          fontSize: "12px",
          fontFamily: "Outfit, sans-serif",
        },
        offsetY: 5,
        rotateAlways: chartData.labels.length > 20,
        hideOverlappingLabels: !isHourlyData, // Mostrar todos os labels para dados horários
        minHeight: 30,
        trim: false,
      },
      axisBorder: {
        show: false, // Hide x-axis border
      },
      axisTicks: {
        show: false, // Hide x-axis ticks
      },
      tickAmount: isHourlyData ? Math.min(chartData.labels.length, 24) : undefined,
    },
    yaxis: {
      labels: {
        style: {
          fontSize: "12px", // Adjust font size for y-axis labels
          colors: ["#6B7280"], // Color of the labels
          fontFamily: "Outfit, sans-serif",
        },
        formatter: function (value) {
          // Formatar valores para o formato brasileiro com R$
          if (value >= 1000) {
            return "R$ " + (value / 1000).toFixed(1) + "k";
          }
          return "R$ " + value.toFixed(0);
        },
        offsetX: -15, // Afastar os números do eixo Y do gráfico
      },
      title: {
        text: "", // Remove y-axis title
        style: {
          fontSize: "0px",
        },
      },
    },
  };

  const series = [
    {
      name: "Vendas",
      data: chartData.datasets.netRevenue,
    },
  ];

  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-5 pb-5 pt-5 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6 sm:pt-6">
      <div className="flex flex-col gap-5 mb-6 sm:flex-row sm:justify-between">
        <div className="w-full">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Evolução de Receita
          </h3>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center" style={{ height: "310px" }}>
          <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="w-full overflow-hidden">
          <Chart options={options} series={series} type="area" height={500} />
        </div>
      )}
    </div>
  );
}
